import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { AuthService } from 'auth';
import { I18nService, TranslatePipe } from 'i18n';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { marketingPoolOf } from '../../services/marketing-pool-access';
import { MarketingPoolService, MpInvestment, MpOverview } from '../../services/marketing-pool.service';

const LIVE: ReadonlyArray<MpInvestment['status']> = ['locked', 'active', 'due'];

/**
 * Dashboard card of the Marketing Pool (spec §14): ONLY for holders of
 * participations (the partner's own unaccepted draft counts) or referrers with
 * referrals — never for everyone. Deliberately no "invest now" prompt for
 * unlocked partners without a participation: the menu entry leads them to the
 * self-service form (customer correction 2026-10-28). The overview is
 * fetched only when the payload says the user may see the product or is a
 * referrer (or, once the backend sends it, holds investments); the card renders
 * only when that overview actually contains investments or referrals.
 */
@Component({
  selector: 'app-marketing-pool-card',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslatePipe],
  template: `
    @if (overview(); as o) {
      @if (o.investments.length || o.referrals.length) {
        <section class="card family-block mp-card" aria-labelledby="mp-card-h">
          <div class="family-block__head">
            <h3 id="mp-card-h">{{ 'marketing_pool.card.title' | translate }}</h3>
            <a [routerLink]="['/', lang(), 'marketing-pool']">{{ 'marketing_pool.card.open' | translate }}</a>
          </div>
          @if (offered()) {
            <p class="mp-card__offer">
              {{ 'marketing_pool.card.offer_waiting' | translate:{ count: offered() } }}
              <a [routerLink]="['/', lang(), 'marketing-pool']">{{ 'marketing_pool.card.offer_review' | translate }}</a>
            </p>
          }
          <div class="family-block__counters mp-card__counters">
            <div><b>{{ o.investments.length - offered() }}</b><span>{{ 'marketing_pool.card.investments' | translate }}</span></div>
            <div><b>{{ nextDue() ? date(nextDue()) : '—' }}</b><span>{{ 'marketing_pool.card.next_due' | translate }}</span></div>
            <div><b>{{ money(openClaim()) }}</b><span>{{ 'marketing_pool.card.open_claim' | translate }}</span></div>
            @if (o.referrals.length) {
              <div><b>{{ o.referrals.length }}</b><span>{{ 'marketing_pool.card.referrals' | translate }}</span></div>
            }
          </div>
        </section>
      }
    }
  `,
  styles: [`
    .mp-card__counters b { font-size: 1.1rem; overflow-wrap: anywhere; }
    .mp-card__offer { font-weight: 600; margin: .25rem 0 .5rem; }
    .mp-card__offer a { margin-left: .35rem; }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MarketingPoolCardComponent {
  private auth       = inject(AuthService);
  private service    = inject(MarketingPoolService);
  private i18n       = inject(I18nService);
  private destroyRef = inject(DestroyRef);

  overview = signal<MpOverview | null>(null);

  /** The partner's own drafts not accepted yet (status `offered`). */
  offered = computed(() => (this.overview()?.investments ?? []).filter(i => i.status === 'offered').length);

  /** Earliest regular payout date of a running participation. */
  nextDue = computed(() => {
    const dates = (this.overview()?.investments ?? [])
      .filter(i => LIVE.includes(i.status) && (i.payout_date || i.due_at))
      .map(i => (i.payout_date || i.due_at) as string)
      .sort();
    return dates[0] ?? null;
  });

  /** Remaining claim over all running participations (`open_total`, else the agreed total). */
  openClaim = computed(() => (this.overview()?.investments ?? [])
    .filter(i => LIVE.includes(i.status))
    .reduce((sum, i) => sum + Number(i.open_total ?? i.total_payout_usd ?? 0), 0));

  constructor() {
    this.auth.getUser$()
      .pipe(
        map(user => {
          const mp = marketingPoolOf(user);
          return !!user && (mp.visible || mp.referrer || !!mp.holder);
        }),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(eligible => {
        if (!eligible) { this.overview.set(null); return; }
        this.service.overview().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: o => this.overview.set(o),
          error: () => this.overview.set(null)
        });
      });
  }

  money(value: number): string {
    return new Intl.NumberFormat(this.lang(), { style: 'currency', currency: 'USD' }).format(value);
  }

  date(value: string | null): string {
    if (!value) return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString(this.lang(), { year: 'numeric', month: 'short', day: 'numeric' });
  }

  lang(): string {
    return this.i18n.getLanguage() || 'en';
  }
}
